import { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import LookupSelect, { LookupOption } from '../../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function ReprocessRecalculate() {
  const [mode, setMode] = useState<'reprocess' | 'recalculate'>('reprocess');
  const [scope, setScope] = useState<'individual' | 'department'>('individual');
  const [employeeId, setEmployeeId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    api.get('/masters/departments').then((r) => setDepartments(r.data.departments || []));
  }, []);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const body: any = { fromDate, toDate };
      if (scope === 'individual' && employeeId) body.employeeIds = [employeeId];
      if (scope === 'department' && departmentId) body.departmentId = departmentId;
      const res = await api.post(`/tms/attendance/${mode}`, body);
      setResult(res.data.summary);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to run');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Reprocess / Recalculate Attendance</h1>
      <p className="text-sm text-gray-600 mb-4 max-w-2xl">
        <strong>Reprocess</strong> rebuilds attendance from the raw clocking data using the latest shift setup —
        this overwrites manual edits unless the row is locked. <strong>Recalculate</strong> keeps the stored
        In/Out times (including manual edits) and only redoes lateness/OT/allowance calculations. Both skip
        locked rows and are logged as a background task with a bell notification.
      </p>

      <div className="bg-white rounded-lg shadow p-4 space-y-4 max-w-2xl">
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={mode === 'reprocess'} onChange={() => setMode('reprocess')} /> Reprocess
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={mode === 'recalculate'} onChange={() => setMode('recalculate')} /> Recalculate
          </label>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={scope === 'individual'} onChange={() => setScope('individual')} /> Individual
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={scope === 'department'} onChange={() => setScope('department')} /> Department
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={scope !== 'individual' && scope !== 'department'} onChange={() => { setScope('department'); setDepartmentId(''); }} /> All Active Employees
          </label>
        </div>
        {scope === 'individual' && (
          <LookupSelect label="Employee" value={employeeId} onChange={setEmployeeId} fetchOptions={fetchEmployeeOptions} placeholder="Search employee..." />
        )}
        {scope === 'department' && (
          <Select label="Department (blank = all active employees)" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} options={departments.map((d) => ({ value: d.id, label: d.name }))} />
        )}
        <div className="grid grid-cols-2 gap-4">
          <Input label="From Date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <Input label="To Date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <Button onClick={run} loading={running} className="w-full">Run {mode === 'reprocess' ? 'Reprocess' : 'Recalculate'}</Button>

        {result && (
          <div className="bg-gray-50 rounded-lg p-3 text-xs">
            <pre>{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
